import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ArrowLeft, TrendingDown, Clock, Star, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const user = localStorage.getItem('user');
  if (user) {
    const userData = JSON.parse(user);
    if (userData.token) {
      return { Authorization: `Bearer ${userData.token}` };
    }
  }
  return {};
};

const QuoteViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { service, serviceRequestId } = location.state || {};
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    if (!serviceRequestId) {
      navigate('/customer/dashboard');
      return;
    }
    fetchQuotes();
    // Poll for new quotes every 5 seconds
    const interval = setInterval(fetchQuotes, 5000);
    return () => clearInterval(interval);
  }, [serviceRequestId]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(
        `${API}/service-requests/${serviceRequestId}/quotes`,
        { headers: getAuthHeader() }
      );
      setQuotes(response.data.quotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async (quoteId, price) => {
    setAccepting(quoteId);
    try {
      await axios.post(
        `${API}/service-requests/${serviceRequestId}/accept-quote/${quoteId}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast({
        title: 'Quote Accepted!',
        description: `Driver will be on their way`
      });
      
      navigate('/customer/tracking', {
        state: {
          service,
          serviceRequestId,
          estimatedCost: price
        }
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to accept quote',
        variant: 'destructive'
      });
    } finally {
      setAccepting(null);
    }
  };

  if (!service) {
    navigate('/customer/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-wheely-navy-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-wheely-navy-950 to-wheely-navy-900 border-b border-wheely-orange/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/customer/dashboard')}
              className="text-wheely-orange hover:text-wheely-orange-light hover:bg-wheely-orange/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={fetchQuotes}
              className="border-wheely-orange/50 text-wheely-orange hover:bg-wheely-orange/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Quotes for {service.name}</h2>
          <p className="text-gray-400">Drivers are sending you competitive quotes</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading quotes...</p>
          </div>
        ) : quotes.length === 0 ? (
          <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-bold text-white mb-2">Waiting for Quotes...</h3>
              <p className="text-gray-400">Drivers are reviewing your request. Quotes will appear here.</p>
              <p className="text-gray-500 text-sm mt-4">This page refreshes automatically</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-wheely-orange mb-4">
              <TrendingDown className="w-5 h-5" />
              <p className="font-semibold">Sorted by price - Lowest First</p>
            </div>

            {quotes.map((quote, index) => (
              <Card
                key={quote.id}
                className={`bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-2 ${
                  index === 0 ? 'border-green-500' : 'border-wheely-orange/20'
                } hover:border-wheely-orange/50 transition-colors`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-16 h-16 border-2 border-wheely-orange">
                        <AvatarFallback className="bg-wheely-orange text-white text-xl">
                          {quote.driver_name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-white text-xl mb-1 flex items-center gap-2">
                          {quote.driver_name}
                          {index === 0 && (
                            <Badge className="bg-green-500 text-white">Lowest Price</Badge>
                          )}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-wheely-orange fill-wheely-orange" />
                            <span className="text-white font-semibold">{quote.driver_rating.toFixed(1)}</span>
                          </div>
                          <span className="text-gray-400 text-sm">• {quote.driver_total_jobs} jobs</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm">Quote</p>
                      <p className="text-green-500 font-bold text-4xl">£{quote.price.toFixed(2)}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>Estimated arrival: {quote.estimated_arrival_minutes} minutes</span>
                  </div>
                  
                  {quote.message && (
                    <div className="bg-wheely-navy-900 rounded-lg p-4">
                      <p className="text-gray-400 text-sm mb-1">Message from driver:</p>
                      <p className="text-white">{quote.message}</p>
                    </div>
                  )}

                  <Button
                    onClick={() => handleAcceptQuote(quote.id, quote.price)}
                    disabled={accepting === quote.id}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold text-lg py-6"
                  >
                    {accepting === quote.id ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Accepting...
                      </span>
                    ) : (
                      `Accept Quote - £${quote.price.toFixed(2)}`
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteViewer;